import React, { useCallback, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { flushSync } from 'react-dom'
import { omitBy, toArray } from 'lodash-es'
import { GetOrdersReq, TabType, OutboundWaitingUrlParams, isB2CParams, B2CUrlParams } from '@/services/outbound/outboundWaitingStatus/types'
import { useB2COutboundData } from '../hooks/useOutboundWaitingData'
import B2CContent from './B2CContent'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useShipperFieldSelection } from '@/components/system/ShipperSelector'
import { SHIPPER_TYPES } from '@/services/shipper/apis'

interface B2CContentWrapperProps {
  parsedQs: B2CUrlParams & { isShipperNamesSelectedAll?: boolean }
  filteredWarehouses?: string[]
  replaceQuery: (query: any) => void
  reload: () => void
  currentTabType: TabType
}

export const getB2CInitForm = (parsedQs: Partial<B2CUrlParams> = {}): GetOrdersReq => {
  return {
    cutType: parsedQs.tabType as TabType,
    shipperNames: [],
    logisticsServiceTypes: parsedQs.logisticsServiceTypes
      ? Array.isArray(parsedQs.logisticsServiceTypes)
        ? parsedQs.logisticsServiceTypes
        : [parsedQs.logisticsServiceTypes]
      : [],
    orderNumbers: parsedQs.orderNumbers ? (Array.isArray(parsedQs.orderNumbers) ? parsedQs.orderNumbers : [parsedQs.orderNumbers]) : [],
  }
}

function B2CContentWrapper({ parsedQs, replaceQuery, reload, currentTabType, filteredWarehouses }: B2CContentWrapperProps) {
  const b2cParams = isB2CParams(parsedQs) ? parsedQs : { tabType: 'B2C' as const }

  const form = useForm<GetOrdersReq>({
    defaultValues: getB2CInitForm(b2cParams),
  })

  const [queryKey, setQueryKey] = useState(getB2CInitForm(b2cParams))

  const { isAllSelected, handleShipperFieldChange, resetShipperSelection, processShipperDataForSubmit } = useShipperFieldSelection<GetOrdersReq>({
    logisticsServiceTypes: parsedQs?.logisticsServiceTypes?.length === SHIPPER_TYPES.length,
    shipperNames: String(parsedQs?.isShipperNamesSelectedAll) === 'true',
  })

  const { data, expectedAllocationData, currentPage, isLoading, onChangePage, refetchAllData, setCurrentPage } = useB2COutboundData({
    ...queryKey,
    isAllSelected,
  })

  const handleReset = useCallback(() => {
    form.reset(getB2CInitForm())
    resetShipperSelection()
  }, [form, resetShipperSelection, b2cParams])

  const handleSubmit = useCallback(
    async (formData: GetOrdersReq) => {
      const query = omitBy(formData, (value) => value === '') as GetOrdersReq

      // 현재 탭의 search와 tabSearches를 함께 업데이트
      MenuStorage.menuOnSession.setTabSearch(currentTabType, { ...query, isShipperNamesSelectedAll: isAllSelected.shipperNames })

      // TODO: [SCOPE B] 아래 다건 입력 구조 데이터 타입을 배열 -> 스트링으로 변환 필요 (API 수정 후 아래 코드 삭제 필요)
      if (typeof query?.orderNumbers === 'string') {
        query.orderNumbers = [query?.orderNumbers]
      }

      flushSync(() => {
        setQueryKey({ ...query, logisticsServiceTypes: formData.logisticsServiceTypes || [] })
        setCurrentPage(0)
      })

      reload()
      await refetchAllData()
      replaceQuery({ ...query, tabType: currentTabType, isShipperNamesSelectedAll: isAllSelected.shipperNames })
    },
    [currentTabType, reload, replaceQuery, refetchAllData, setCurrentPage, processShipperDataForSubmit],
  )

  return (
    <FormProvider {...form}>
      <B2CContent
        data={data}
        expectedAllocationData={expectedAllocationData}
        filteredWarehouses={filteredWarehouses}
        currentPage={currentPage}
        isLoading={isLoading}
        parsedQs={parsedQs}
        onSubmit={handleSubmit}
        onChangePage={onChangePage}
        onFieldChange={handleShipperFieldChange}
        onReset={handleReset}
      />
    </FormProvider>
  )
}

export default B2CContentWrapper
